import { useRef, useState } from 'react';
import { dismiss, notifyProblems } from '../lib/errorNotices';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MAX_IMPORT_BYTES,
  canOpenReconcile,
  canPostJournals,
  formatNzDateOr,
  parseRef,
} from '@promanage/core';
import { Amount, Button, Chip, Field, Panel, PanelHeader, Ref, Select } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { ReconcileNav } from '../features/reconcile/ReconcileNav';
import { requireAccess } from '../features/auth/routeAccess';
import { Issues } from '../features/import/Issues';
import { useSession } from '../features/auth/session';
import { needsStepUp, trpc, type RouterOutputs } from '../lib/trpc';

export const Route = createFileRoute('/reconcile/import')({
  beforeLoad: requireAccess(canOpenReconcile),
  component: ImportStatementPage,
});

/**
 * Importing a bank statement.
 *
 * THREE CHOICES AND A BUTTON: which account, which format, which file. Everything that makes
 * this possible for a bank nobody wrote code for was settled on the formats screen; here the
 * operator is doing the monthly job, and the monthly job should be three clicks.
 *
 * THE PREVIEW IS THE SAFETY. Nothing is written until it has been read and shown: how many lines
 * would land, how many the account already holds, what came in and what went out, and the lines
 * themselves. The count on the button is the count that was approved — if it changes between
 * looking and pressing, the server refuses rather than writing a number nobody saw.
 *
 * NOTHING HERE POSTS A JOURNAL. An imported line is what the BANK says happened; our books say
 * what we say happened; the reconciliation next door is the comparison. That is why this screen
 * sits beside /reconcile and not in the ledger.
 */

/** One notice for the chosen file's own problems, replaced when another file is chosen. */
const READ_PROBLEM = 'import-file';
function ImportStatementPage() {
  const session = useSession();
  const mayImport = canPostJournals(session?.roles ?? []);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const accounts = useQuery(
    trpc.reconciliation.accounts.queryOptions(undefined, { staleTime: 30_000 }),
  );
  const formats = useQuery(
    trpc.statementImport.formats.queryOptions(undefined, { staleTime: 30_000 }),
  );
  const imports = useQuery(
    trpc.statementImport.imports.queryOptions(undefined, { staleTime: 10_000 }),
  );

  const usable = (formats.data ?? []).filter((format) => format.status === 'active');

  const [accountId, setAccountId] = useState<string | null>(null);
  const [formatId, setFormatId] = useState<string | null>(null);
  const [file, setFile] = useState<{ name: string; csv: string } | null>(null);

  const account =
    accounts.data?.accounts.find((a) => a.id === accountId) ?? accounts.data?.accounts[0];
  const format = usable.find((f) => f.id === formatId) ?? usable[0];

  const preview = useMutation(
    trpc.statementImport.preview.mutationOptions({
      meta: { errorTitle: 'That file could not be read' },
      onError(error) {
        // Fifteen minutes have passed since the last code. Not something to explain in the red
        // box below — send them to the prompt and let them come back to finish the import.
        if (needsStepUp(error)) void navigate({ to: '/mfa' });
      },
    }),
  );
  /** What the last import did, kept so the screen can say so after it clears the file. */
  const [landed, setLanded] = useState<Landed | null>(null);

  const commit = useMutation(
    trpc.statementImport.commit.mutationOptions({
      meta: { errorTitle: 'Nothing was imported' },
      onSuccess(result) {
        // The account's outstanding list and every reconciliation figure downstream of it are
        // now describing a statement they have not seen.
        void queryClient.invalidateQueries();
        setFile(null);
        preview.reset();
        /*
         * SAY WHAT HAPPENED, and where to go next.
         *
         * The screen's primary action used to end in silence — the file cleared, the preview
         * reset, and the only evidence was a new row appearing in the history below. This is the
         * one moment the operator has just done something they cannot casually undo, and the one
         * moment they most need the next step named: the import is not the job, it is the start
         * of it.
         */
        setLanded({
          ref: result.ref,
          importedCount: result.importedCount,
          duplicateCount: result.duplicateCount,
          matched: result.matched.staged,
          refused: result.matched.refused,
        });
      },
      onError(error) {
        if (needsStepUp(error)) void navigate({ to: '/mfa' });
      },
    }),
  );

  /** A preview stops meaning anything the moment any of its three inputs change. */
  const reset = () => {
    preview.reset();
    commit.reset();
    // And so does the last import's result: choosing a new file means the operator has moved on
    // from it, and a stale "312 lines imported" above a fresh preview names the wrong statement.
    setLanded(null);
  };

  const checked = preview.data !== undefined && !preview.isPending;
  const ready =
    checked && preview.data!.issues.length === 0 && preview.data!.formatIssues.length === 0;

  if (!mayImport) {
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="Statement import" />
        <ReconcileNav />
        {/* A statement is the evidence a trust reconciliation is judged against. */}
        <Panel className="p-6">
          <h2 className="text-[1.0625rem] font-bold">Needs a trust accounting role</h2>
          <p className="mt-1 text-[0.8125rem] text-ink-muted">
            Ask someone with that role to import the statement.
          </p>
        </Panel>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Statement import"
        subtitle="A bank CSV, to reconcile against the books"
        actions={
          <Link to="/settings/statements">
            <Button size="sm" variant="secondary">
              Statement formats
            </Button>
          </Link>
        }
      />
      <ReconcileNav />

      {/* The page's own panels are a stack; the bar and strip above space like every page's. */}
      <div className="flex flex-col gap-4">
        {landed ? <Landing landed={landed} /> : null}

        {/*
         * THE MONTHLY JOB AND ITS HISTORY SIDE BY SIDE. The history used to sit under the
         * preview, a long scroll away; beside the form it answers "did I already import March?"
         * before the file is chosen.
         */}
        <div className="grid items-start gap-4 lg:grid-cols-5">
          <div className="lg:col-span-3">
            {usable.length === 0 && !formats.isLoading ? (
              // Every bank writes a different CSV, so each needs telling which column is which — once.
              <Panel className="p-5">
                <PanelHeader
                  title="Set up a format first"
                  subtitle="Which column is which, once per bank"
                  className="p-0"
                />
                <Link to="/settings/statements" className="mt-3 inline-block">
                  <Button size="sm">Add statement format</Button>
                </Link>
              </Panel>
            ) : (
              <Panel className="p-0">
                <PanelHeader title="Import a statement" />

                <div className="grid gap-x-4 gap-y-3 border-t border-rule px-5 py-4 sm:grid-cols-2">
                  <Field label="Bank account" required>
                    {(a) => (
                      <Select
                        {...a}
                        value={account?.id ?? ''}
                        onChange={(e) => {
                          setAccountId(e.target.value);
                          reset();
                        }}
                      >
                        {(accounts.data?.accounts ?? []).map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.name}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>

                  <Field label="Statement format" required>
                    {(a) => (
                      <Select
                        {...a}
                        value={format?.id ?? ''}
                        onChange={(e) => {
                          setFormatId(e.target.value);
                          reset();
                        }}
                      >
                        {usable.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.name}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                </div>

                <div className="border-t border-rule px-5 py-4">
                  <ChooseFile
                    file={file}
                    onChoose={(chosen) => {
                      if (!chosen) return;
                      dismiss(READ_PROBLEM);
                      reset();
                      if (chosen.size > MAX_IMPORT_BYTES) {
                        notifyProblems(
                          'Could not read that file',
                          [
                            `That file is ${Math.round(chosen.size / 1024)}KB and the limit is ${Math.round(MAX_IMPORT_BYTES / 1024)}KB. Export it a month at a time — importing the same period twice is safe.`,
                          ],
                          READ_PROBLEM,
                        );
                        return;
                      }
                      chosen
                        .text()
                        .then((csv) => setFile({ name: chosen.name, csv }))
                        .catch(() =>
                          notifyProblems(
                            'Could not read that file',
                            ['That file could not be read. Save it again as CSV and retry.'],
                            READ_PROBLEM,
                          ),
                        );
                    }}
                    onClear={() => {
                      setFile(null);
                      reset();
                    }}
                  />

                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <Button
                      disabled={!file || !account || !format || preview.isPending}
                      onClick={() =>
                        file &&
                        account &&
                        format &&
                        preview.mutate({
                          ledgerAccountId: account.id,
                          statementFormatId: format.id,
                          csv: file.csv,
                        })
                      }
                    >
                      {preview.isPending ? 'Reading' : 'Read the file'}
                    </Button>
                    <span className="text-[0.8125rem] text-ink-muted">
                      You’ll see what it contains before anything is saved.
                    </span>
                  </div>
                </div>
              </Panel>
            )}
          </div>

          <div className="lg:col-span-2">
            <History
              rows={imports.data ?? []}
              loading={imports.isLoading}
              onDiscarded={() => void queryClient.invalidateQueries()}
            />
          </div>
        </div>

        {checked && preview.data!.formatIssues.length > 0 ? (
          <Panel className="p-5">
            <h2 className="text-[0.9375rem] font-bold">
              This file does not match “{preview.data!.formatName}”
            </h2>
            <ul className="mt-2 flex list-disc flex-col gap-1 pl-5 text-[0.8125rem] text-ink-muted">
              {preview.data!.formatIssues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
            {/* Edit rather than add a second format: every future import follows the edit. */}
            <p className="mt-3 text-[0.8125rem] text-ink-muted">
              If the bank changed its export, edit the format.
            </p>
            <Link to="/settings/statements" className="mt-3 inline-block">
              <Button size="sm" variant="secondary">
                Edit the format
              </Button>
            </Link>
          </Panel>
        ) : null}

        {ready ? (
          <Verdict
            data={preview.data!}
            fileName={file?.name ?? ''}
            importing={commit.isPending}
            onImport={() =>
              file &&
              commit.mutate({
                ledgerAccountId: preview.data!.ledgerAccountId,
                statementFormatId: preview.data!.formatId,
                csv: file.csv,
                fileName: file.name,
                expectedImported: preview.data!.importedCount,
              })
            }
          />
        ) : null}

        {checked && preview.data!.issues.length > 0 ? (
          <Issues issues={preview.data!.issues} />
        ) : null}
      </div>
    </div>
  );
}

// ── What the import did, and what happens next ───────────────────────────────

interface Landed {
  ref: string;
  importedCount: number;
  duplicateCount: number;
  /** Lines the cascade was sure of and staged. Nothing is posted until the batch is processed. */
  matched: number;
  /** Lines it was sure of and could not even stage. Named, because they are still in the queue. */
  refused: number;
}

/**
 * The confirmation, and the next step.
 *
 * WHY IT SAYS THREE THINGS. The file landed, the matcher has already been over it, and the queue
 * is where the rest is worked — and the third is the one an operator new to this does not know.
 * Importing is not the job; it is the start of it, and a screen that clears its own file and says
 * nothing leaves somebody looking at an empty form wondering whether it worked.
 *
 * NOT AN ALERT. Nothing went wrong, so it is not toned as though something did — DESIGN_SYSTEM
 * §2's rule that red means error and destruction, kept on the happy path too.
 *
 * IT NAMES WHAT IS STILL WAITING rather than claiming completion. "240 matched" would read as
 * finished; the batch is staged and posts nothing until somebody presses Process, and the link
 * goes to the screen where both of those are true and visible.
 */
function Landing({ landed }: { landed: Landed }) {
  return (
    <Panel className="border-l-2 border-l-accent p-5">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-[0.9375rem] font-bold">
          {landed.importedCount.toLocaleString('en-NZ')} line
          {landed.importedCount === 1 ? '' : 's'} imported
        </h2>
        <span className="font-mono text-[0.75rem] text-ink-muted">{landed.ref}</span>
        {landed.duplicateCount > 0 ? (
          <span className="text-[0.8125rem] text-ink-muted">
            {landed.duplicateCount} the account already held
          </span>
        ) : null}
      </div>

      <p className="mt-1.5 max-w-prose text-[0.8125rem] text-ink-muted">
        {landed.matched > 0 ? (
          <>{landed.matched} matched and staged — nothing posts until you process the batch.</>
        ) : (
          // Never "0 matched", which reads as a failure. The honest statement is that the lines
          // are waiting for a person, which is what the queue is for.
          <>Every line is waiting in the review queue.</>
        )}
        {landed.refused > 0 ? (
          <> {landed.refused} refused by the books — in the queue with the reason.</>
        ) : null}
      </p>

      <div className="mt-3 flex flex-wrap gap-2">
        <Link to="/reconcile/queue">
          <Button size="sm">Work the review queue</Button>
        </Link>
        <Link to="/reconcile">
          <Button size="sm" variant="secondary">
            Reconcile a day
          </Button>
        </Link>
      </div>
    </Panel>
  );
}

// ── The verdict ──────────────────────────────────────────────────────────────

/**
 * The preview's shape, taken from the router rather than restated.
 *
 * `RouterOutputs` is inferred from the server's own implementation, so a field that changes name
 * on the API breaks this file at compile time instead of rendering `undefined` at runtime. That
 * is the entire reason the boundary is tRPC — restating the shape here would give it away.
 */
type Preview = RouterOutputs['statementImport']['preview'];

const SHOWN = 100;

function Verdict({
  data,
  fileName,
  importing,
  onImport,
}: {
  data: Preview;
  fileName: string;
  importing: boolean;
  onImport: () => void;
}) {
  const [all, setAll] = useState(false);
  const lines = all ? data.lines : data.lines.slice(0, SHOWN);

  return (
    <Panel className="p-0">
      <PanelHeader
        title={`${data.importedCount.toLocaleString('en-NZ')} line${data.importedCount === 1 ? '' : 's'} would be imported`}
        subtitle={`${fileName} · read by ${data.formatName} · into ${data.accountName}`}
        aside={
          data.duplicateCount > 0 ? (
            <Chip tone="info">{data.duplicateCount} already on the account</Chip>
          ) : null
        }
      />

      {/*
       * Money in and money out, never netted. A statement that brought in $48,000 and paid out
       * $47,900 is not "$100 arrived", and an operator checking a total against their bank is
       * checking two figures.
       *
       * These describe what would be WRITTEN, not what the file holds — a total including lines
       * the account already has would be the wrong number to approve.
       */}
      <dl className="grid grid-cols-2 gap-px border-t border-rule bg-rule sm:grid-cols-4">
        <Stat
          label="Money in"
          value={<Amount value={BigInt(data.inCents)} tone="positive" currency />}
        />
        <Stat label="Money out" value={<Amount value={BigInt(data.outCents)} currency />} />
        <Stat
          label="Statement covers"
          value={
            <span className="text-[0.9375rem] font-bold">
              {formatNzDateOr(data.firstDate, '—')} – {formatNzDateOr(data.lastDate, '—')}
            </span>
          }
        />
        <Stat
          label="Rows read"
          value={<span className="text-[0.9375rem] font-bold tabular-nums">{data.rowCount}</span>}
        />
      </dl>

      {/*
       * Rows read fine and deliberately not imported — a $0.00 line, or a balance or total row
       * around the table.
       *
       * Said out loud rather than left as arithmetic that does not add up. "95 rows read, 94
       * imported" with nothing to explain the difference reads as a bug, and the operator has no
       * way to tell which line went where. Not an error, so not styled as one: these refuse
       * nothing, which is the change — one such row used to refuse the whole statement.
       */}
      <Balances balances={data.balances} />

      {data.skipped.length > 0 ? <Skipped rows={data.skipped} /> : null}

      {/*
       * A balance column that will not read. NEITHER an issue nor a skip — the lines import in
       * full, without the convenience figure — so it is said in the same neutral voice as the
       * $0.00 rows above rather than dressed as a fault.
       *
       * Said at all because the alternative is silence: a column mapped one place to the left
       * imports perfectly and simply never offers a closing balance, and nobody would connect the
       * two. This is where that is discoverable.
       */}
      {data.balanceIssues.length > 0 ? (
        <p className="border-t border-rule px-5 py-3 text-[0.8125rem] text-ink-muted">
          {/* The lines still import, just without a closing balance; if none read, the format
              probably points at the wrong column. */}
          Balance unreadable on row{data.balanceIssues.length === 1 ? '' : 's'}{' '}
          {data.balanceIssues.map((row) => row.row).join(', ')} — they still import, without a
          closing balance.
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-3 border-t border-rule px-5 py-4">
        <Button disabled={importing || data.importedCount === 0} onClick={onImport}>
          {importing
            ? 'Importing'
            : `Import ${data.importedCount.toLocaleString('en-NZ')} line${data.importedCount === 1 ? '' : 's'}`}
        </Button>
        <span className="text-[0.8125rem] text-ink-muted">
          {data.importedCount === 0
            ? 'Every line is already on the account.'
            : 'All lines or none.'}
        </span>
      </div>

      {/*
       * The lines themselves. `in`/`out` is stated in the caption because this product has two
       * opposite debit/credit conventions and this table is in the bank's, not the ledger's.
       */}
      <div className="overflow-x-auto print:overflow-visible">
        {/* Scrolls inside its card on a phone, rather than widening the page past the screen. */}
        <table className="w-full border-t border-rule text-[0.8125rem]">
          <caption className="px-5 py-2 text-left text-[0.75rem] text-ink-subtle">
            As the bank states it — “in” is money arriving in the account.
          </caption>
          <thead>
            <tr className="bg-surface-sunk text-[0.75rem] font-medium text-ink-muted">
              <th scope="col" className="px-5 py-2 text-left">
                Row
              </th>
              <th scope="col" className="px-3 py-2 text-left">
                Date
              </th>
              <th scope="col" className="px-3 py-2 text-left">
                Reference
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                In
              </th>
              <th scope="col" className="px-5 py-2 text-right">
                Out
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            {lines.map((line) => (
              <tr key={line.row} className={line.duplicate ? 'text-ink-subtle' : ''}>
                <td className="px-5 py-2 tabular-nums">{line.row}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {formatNzDateOr(line.occurredOn, '')}
                </td>
                <td className="px-3 py-2">
                  <span className="line-clamp-1">{line.reference}</span>
                  {line.duplicate ? (
                    <span className="text-[0.75rem] text-ink-subtle">Already on the account</span>
                  ) : null}
                </td>
                {/* The unused side is blank, not 0.00 — a column of zeroes is noise to scan past. */}
                <td className="px-3 py-2 text-right">
                  {line.direction === 'in' ? (
                    <Amount
                      value={BigInt(line.amountCents)}
                      tone={line.duplicate ? 'neutral' : 'positive'}
                    />
                  ) : null}
                </td>
                <td className="px-5 py-2 text-right">
                  {line.direction === 'out' ? <Amount value={BigInt(line.amountCents)} /> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.lines.length > SHOWN ? (
        <div className="border-t border-rule px-5 py-3">
          <Button size="sm" variant="ghost" onClick={() => setAll((v) => !v)}>
            {all ? 'Show fewer' : `Show all ${data.lines.length} lines`}
          </Button>
        </div>
      ) : null}
    </Panel>
  );
}

/**
 * The statement's own two ends, and whether its lines were proven against them.
 *
 * ACROSS THE WHOLE FILE, unlike the money in and out above it, which count only what would be
 * written. That is deliberate: this is the file proving itself complete, and a line the account
 * already holds is still a line the statement's arithmetic needs.
 *
 * Only the proven state is ever shown. A statement that did not add up is refused, and its rows
 * are in the list of what needs fixing instead.
 */
function Balances({ balances }: { balances: Preview['balances'] }) {
  if (balances.opening === null && balances.closing === null) {
    return (
      <p className="border-t border-rule px-5 py-3 text-[0.8125rem] text-ink-muted">
        No balances in this file to check it against. Map the running balance in the statement
        format and every import proves itself.
      </p>
    );
  }

  const net = BigInt(balances.netCents);
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-rule px-5 py-3 text-[0.8125rem]">
      {balances.opening ? (
        <span>
          <span className="text-ink-subtle">Opens </span>
          <Amount value={BigInt(balances.opening.balanceCents)} currency tone="neutral" />
        </span>
      ) : null}
      <span>
        <span className="text-ink-subtle">Lines </span>
        <span className="font-semibold tabular-nums">{net < 0n ? '−' : '+'}</span>
        <Amount value={net < 0n ? -net : net} currency tone="neutral" />
      </span>
      {balances.closing ? (
        <span>
          <span className="text-ink-subtle">Closes </span>
          <Amount value={BigInt(balances.closing.balanceCents)} currency tone="neutral" />
          <span className="text-ink-subtle"> on {formatNzDateOr(balances.closing.on, '—')}</span>
        </span>
      ) : null}
      {balances.proven ? <Chip tone="positive">Adds up</Chip> : null}
    </div>
  );
}

const SKIP_GROUPS: { kinds: readonly Preview['skipped'][number]['kind'][]; label: string }[] = [
  { kinds: ['above', 'below'], label: 'Balance, heading and total rows' },
  { kinds: ['zero'], label: '$0.00 lines' },
];

/**
 * Rows read fine and deliberately not imported, said by kind. Each row's own reason is on hover —
 * the list is for "which rows", the tooltip for "why that one".
 */
function Skipped({ rows }: { rows: Preview['skipped'] }) {
  return (
    <div className="flex flex-col gap-1 border-t border-rule px-5 py-3 text-[0.8125rem] text-ink-muted">
      {SKIP_GROUPS.map((group) => {
        const matching = rows.filter((row) => group.kinds.includes(row.kind));
        if (matching.length === 0) return null;
        return (
          <p key={group.label}>
            Not imported — {group.label.toLowerCase()}: row{matching.length === 1 ? '' : 's'}{' '}
            {matching.map((row, index) => (
              <span key={row.row} title={row.reason} className="tabular-nums">
                {index > 0 ? ', ' : ''}
                {row.row}
              </span>
            ))}
          </p>
        );
      })}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="bg-surface px-5 py-3">
      <dt className="text-[0.75rem] text-ink-subtle">{label}</dt>
      <dd className="mt-0.5">{value}</dd>
    </div>
  );
}

// ── What has been imported before ────────────────────────────────────────────

/**
 * Every import made, and the way back out of one.
 *
 * DISCARDING IS FOR THE FILE IMPORTED AGAINST THE WRONG ACCOUNT — the mistake somebody makes in
 * the first week, when two trust accounts sit next to each other in a dropdown. The lines go and
 * this row stays, marked, because the file WAS imported and a record that quietly vanished would
 * leave a gap in the numbering nobody could account for.
 */
function History({
  rows,
  loading,
  onDiscarded,
}: {
  rows: RouterOutputs['statementImport']['imports'];
  loading: boolean;
  onDiscarded: () => void;
}) {
  const [confirming, setConfirming] = useState<string | null>(null);
  const discard = useMutation(
    trpc.statementImport.discard.mutationOptions({
      meta: { errorTitle: 'That import could not be discarded' },
      onSuccess() {
        setConfirming(null);
        onDiscarded();
      },
    }),
  );

  return (
    <Panel className="p-0">
      <PanelHeader title="Imported statements" />

      {loading ? (
        <p className="border-t border-rule px-5 py-6 text-[0.875rem] text-ink-muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="border-t border-rule px-5 py-6 text-[0.875rem] text-ink-muted">
          No statements imported yet.
        </p>
      ) : (
        <ul className="divide-y divide-rule border-t border-rule">
          {rows.map((row) => {
            // `parseRef` rather than a regex over the string: one function in core agrees with
            // the database's generated column, and `<Ref>` is the only sanctioned way to render
            // one. A ref the parser does not recognise is shown as text rather than guessed at.
            const parsed = parseRef(row.ref);
            return (
              <li key={row.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {parsed ? (
                      <Ref entity={parsed.entity} sequence={parsed.sequence} />
                    ) : (
                      <span className="font-mono text-[0.75rem] text-ink-muted">{row.ref}</span>
                    )}
                    <span className="truncate text-[0.875rem] font-semibold">{row.fileName}</span>
                    {row.discardedAt !== null ? <Chip tone="warn">Discarded</Chip> : null}
                  </div>
                  <p className="mt-0.5 text-[0.75rem] text-ink-subtle">
                    {row.accountName} · read by {row.formatName} ·{' '}
                    {formatNzDateOr(row.firstDate, '—')} – {formatNzDateOr(row.lastDate, '—')} ·{' '}
                    {row.importedCount} line{row.importedCount === 1 ? '' : 's'}
                    {row.duplicateCount > 0 ? `, ${row.duplicateCount} already held` : ''}
                    {row.closingBalanceCents !== null ? (
                      <>
                        {' · closed at '}
                        <Amount value={BigInt(row.closingBalanceCents)} currency tone="neutral" />
                      </>
                    ) : null}
                  </p>
                </div>

                {row.discardedAt === null ? (
                  confirming === row.id ? (
                    <div className="flex items-center gap-1.5">
                      <span className="text-[0.75rem] text-ink-muted">
                        Remove {row.importedCount} line{row.importedCount === 1 ? '' : 's'}?
                      </span>
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={discard.isPending}
                        onClick={() => discard.mutate({ id: row.id })}
                      >
                        {discard.isPending ? 'Discarding' : 'Discard'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setConfirming(null)}>
                        Keep
                      </Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => setConfirming(row.id)}>
                      Discard
                    </Button>
                  )
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

// ── The file ─────────────────────────────────────────────────────────────────

function ChooseFile({
  file,
  onChoose,
  onClear,
}: {
  file: { name: string; csv: string } | null;
  onChoose: (file: File | undefined) => void;
  onClear: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  return (
    <div>
      {/* A drop target, not a control — the button inside it is what a keyboard reaches. */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          onChoose(e.dataTransfer.files[0]);
        }}
        className={`flex flex-col items-start gap-2 rounded-lg border border-dashed px-4 py-5 transition-colors duration-[var(--duration-fast)] ${
          over ? 'border-accent bg-accent-soft' : 'border-rule bg-surface-sunk'
        }`}
      >
        <input
          ref={input}
          type="file"
          accept=".csv,.txt,text/csv,text/plain"
          className="sr-only"
          onChange={(e) => onChoose(e.target.files?.[0])}
        />

        {file ? (
          <Chip tone="positive">{file.name}</Chip>
        ) : (
          <p className="text-[0.8125rem] text-ink-muted">Drop the statement here, or</p>
        )}

        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={() => input.current?.click()}>
            {file ? 'Choose a different file' : 'Choose a file'}
          </Button>
          {file ? (
            <Button size="sm" variant="ghost" onClick={onClear}>
              Clear
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
